import { useStore } from "../app/store.ts";

/**
 * Crop the canvas to the camera frame overlay area.
 *
 * Like `renderAtTargetRes` in `Viewport3D.tsx`, the crop rectangle is
 * derived from the frame's center + the target aspect, never from the
 * frame's independent width/height — so CSS subpixel rounding cannot
 * drift the crop off the visible frame border. On-screen frame extents
 * equal exported file extents.
 */
export function cropToFrame(canvas: HTMLCanvasElement): Promise<Blob | null> {
  const { renderWidth, renderHeight } = useStore.getState();
  const target = { width: renderWidth, height: renderHeight };

  // Fast path: canvas already at the exact target resolution (populated
  // by renderAtTargetRes's offscreen path) — just encode.
  if (canvas.width === target.width && canvas.height === target.height) {
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  }

  // Fallback: crop from a viewport-sized canvas and rescale to target.
  const frame = document.querySelector("[data-camera-frame]");
  if (!frame) {
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  }

  const canvasRect = canvas.getBoundingClientRect();
  const frameRect = frame.getBoundingClientRect();
  const dpr = canvas.width / canvasRect.width;
  const targetAspect = target.width / target.height;

  // Derive the crop's CSS height from targetAspect, not from frameRect.height.
  const cssFrameW = frameRect.width;
  const cssFrameH = cssFrameW / targetAspect;
  const cssCx = (frameRect.left + frameRect.right) / 2 - canvasRect.left;
  const cssCy = (frameRect.top + frameRect.bottom) / 2 - canvasRect.top;
  const cssLeft = cssCx - cssFrameW / 2;
  const cssTop = cssCy - cssFrameH / 2;

  const sx = Math.round(cssLeft * dpr);
  const sy = Math.round(cssTop * dpr);
  const sw = Math.round(cssFrameW * dpr);
  const sh = Math.round(cssFrameH * dpr);

  const offscreen = document.createElement("canvas");
  offscreen.width = sw;
  offscreen.height = sh;
  const ctx = offscreen.getContext("2d");
  if (!ctx)
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);

  if (target.width === sw && target.height === sh) {
    return new Promise((resolve) => offscreen.toBlob(resolve, "image/png"));
  }

  const scaled = document.createElement("canvas");
  scaled.width = target.width;
  scaled.height = target.height;
  const sctx = scaled.getContext("2d");
  if (!sctx)
    return new Promise((resolve) => offscreen.toBlob(resolve, "image/png"));
  sctx.drawImage(offscreen, 0, 0, target.width, target.height);
  return new Promise((resolve) => scaled.toBlob(resolve, "image/png"));
}

/** Return the main viewport canvas for export. */
function getExportCanvas(): HTMLCanvasElement | null {
  // Use the tagged viewport canvas to avoid grabbing a CubeSpinner or other
  // unrelated canvas that may appear earlier in the DOM.
  return document.querySelector<HTMLCanvasElement>(
    "canvas[data-viewport-canvas]",
  );
}

/** Consume the high-res offscreen canvas populated by renderAtTargetRes, if available. */
function consumeExportCanvas(): HTMLCanvasElement | null {
  const c = window.__poseref_exportCanvas ?? null;
  if (c) window.__poseref_exportCanvas = null;
  return c;
}

/** Trigger a render-for-export, then crop to frame and download as PNG. */
export async function exportToDownload(): Promise<void> {
  const fn = window.__poseref_renderForExport;
  if (fn) fn();
  // Prefer the high-res offscreen canvas (populated by renderAtTargetRes)
  const exportCanvas = consumeExportCanvas();
  const canvas = exportCanvas || getExportCanvas();
  if (!canvas) return;
  const blob = await cropToFrame(canvas);
  if (!blob) return;
  downloadBlob(blob, "poseref-export.png");
}

/** Helper: download a blob as a named PNG file. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Trigger a render-for-export, then share (fallback to download). */
export async function exportToShare(): Promise<void> {
  const fn = window.__poseref_renderForExport;
  if (fn) fn();
  const exportCanvas = consumeExportCanvas();
  const canvas = exportCanvas || getExportCanvas();
  if (!canvas) return;
  const blob = await cropToFrame(canvas);
  if (!blob) return;
  if (navigator.share) {
    try {
      await navigator.share({
        files: [new File([blob], "poseref-export.png", { type: "image/png" })],
      });
      return;
    } catch {
      // fall through to download
    }
  }
  await exportToDownload();
}
